import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Plugins } from '@capacitor/core';
const { Storage } = Plugins;

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly MFA_TOKEN_STORAGE_KEY = "x-mfa-token";
  private BASE_URL: string;
  private NBLOCKS_AUTH_URL: string;
  private NBLOCKS_APP_ID: string;
  private OAUTH_REDIRECT_URI: string;

  constructor(
    private httpClient: HttpClient
  ) {
    this.BASE_URL = environment['restApiUrl'];
    this.NBLOCKS_APP_ID = environment['nblocksAPPId'];
    this.NBLOCKS_AUTH_URL = environment['nblocksAuthApiUrl'];
    this.OAUTH_REDIRECT_URI = environment['oauthCallBackUrl'];
  }

  public async authenticate(username: string, password: string): Promise<{mfaState: 'DISABLED' | 'REQUIRED' | 'SETUP'}>  {
    const {token, mfaState} = await this.httpClient
      .post<{token: string, mfaState: 'DISABLED' | 'REQUIRED' | 'SETUP'}>(`${this.BASE_URL}/auth-proxy/authenticate`, { username, password })
      .toPromise();
    
    if (!token)
      throw new Error("Wrong credentials");

    await this.storeAuthToken(token);
    return {mfaState};
  }

  async handleOauthCallback(authCode: string): Promise<{tenantUserId: string, mfaState: 'DISABLED' | 'REQUIRED' | 'SETUP'}>  {
    const {tenantUserId, authResponse} = await this.httpClient
      .post<{tenantUserId: string, authResponse: {token: string, mfaState: 'DISABLED' | 'REQUIRED' | 'SETUP'}}>(`${this.BASE_URL}/oauth-callback/exchangeToSession`, { authCode })
      .toPromise();
    
    const {token, mfaState} = authResponse;

    if (!token)
      throw new Error("Wrong credentials");

    await this.storeAuthToken(token);
    return {mfaState, tenantUserId};
  }

  async commitMfaCode(mfaCode:string): Promise<void> {
    const result = await this.httpClient.post<{mfaToken: string}>(`${this.BASE_URL}/auth-proxy/commitMfaCode`, {mfaCode}).toPromise();
    await this.storeMfaToken(result.mfaToken);
  }

  async startMfaUserSetup(phoneNumber:string): Promise<void> {
    await this.httpClient.post(`${this.BASE_URL}/auth-proxy/startMfaUserSetup`, {phoneNumber}).toPromise();
  }

  /**
   * Finish setting up MFA for the user and the user is hereby authenticated with MFA aswell.
   * @param mfaCode 
   * @returns The backup code to be saved for future reference
   */
  async finishMfaUserSetup(mfaCode:string): Promise<string> {
    const result = await this.httpClient.post<{mfaToken: string, backupCode: string}>(`${this.BASE_URL}/auth-proxy/finishMfaUserSetup`, {mfaCode}).toPromise();
    await this.storeMfaToken(result.mfaToken);
    return result.backupCode;
  }

  async resetUserMfaSetup(backupCode:string): Promise<void> {
    await this.httpClient.post(`${this.BASE_URL}/auth-proxy/resetUserMfaSetup`, {backupCode}).toPromise();
  }

  async storeAuthToken(token: string): Promise<void> {
    await Storage.set({ key: 'x-auth-token', value: token });
  }

  async storeMfaToken(token: string): Promise<void> {
    await Storage.set({key: this.MFA_TOKEN_STORAGE_KEY, value: token});
  }

  // http://localhost:3070/url/login/633402fef28d8e00252948c8?forceFederation=ms-azure-ad
  public async handleSocialLogin(provider: string): Promise<void> {
    window.location.href = `${this.NBLOCKS_AUTH_URL}/url/login/${this.NBLOCKS_APP_ID}?forceFederation=${provider}&redirectUri=${this.OAUTH_REDIRECT_URI}`;
  }
}
